import { useEffect, useState, useCallback, useRef } from "react";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { apiService } from "../services/apiService";
import { authenticationService } from "../services/authenticationService";
import HelperFunctions from "../helpers/HelperFunctions";
import { Link } from "react-router-dom";
import { useNavigate } from 'react-router-dom';

export default function ErrorModal({ onClose, message, modalSize, forceShow = false }) {

    const [currentUser] = useCurrentUser();
    const { isUserBetaTester } = HelperFunctions();
    const hasLoggedError = useRef(false);

    const [showErrorModal, setShowErrorModal] = useState(false);

    const navigate = useNavigate();
    
    const logFetchError = useCallback(async (user) => {

        if (hasLoggedError.current) return;
        hasLoggedError.current = true;

        if (401 !== message.status && 403 !== message.status) {

            let errormessage = `${message.status} error: ${window.location.href} ${message.url} ${user ? user.email : ''}`;

            let data = new FormData();
            data.append('message', errormessage);

            apiService.apiPostForm(import.meta.env.VITE_API_URL + '/log-fetch-error', data);
        }

    }, [message]);

    useEffect(() => {
        logFetchError(currentUser);
    }, [currentUser, logFetchError]);

    useEffect(() => {
        //if forbidden
        if (401 === message.status || 403 === message.status) {
            // Preserve the query string (utm_*, gclid, etc.) - this redirect fires
            // on page load well before the (interaction-gated) GTM/PostHog pageview
            // capture, so dropping it here silently loses attribution for anyone
            // who lands on a page where the initial data fetch 401s.
            navigate(`/login${window.location.search}`);
            authenticationService.logout();
            //dont show modal if authentication/permission issue
            setShowErrorModal(false);
        } else {
            //only display the error modal to beta users, unless forced (e.g. a
            //render crash caught by the ErrorBoundary, which must show everyone).
            setShowErrorModal(forceShow || isUserBetaTester(currentUser?.roles));
        }
    }, [message, navigate, currentUser, forceShow, isUserBetaTester]);

    const getErrorMessage = () => {

        switch (message.status) {
            case 403:
                return {
                    title: 'PRO Feature',
                    message: 'This is a PRO level feature. Please upgrade your account to access this feature.'
                };
            case 404:
                return {
                    title: 'Not Found',
                    message: 'The requested resource could not be found. Our system administrators have been notified.'
                };
            case 500:
                return {
                    title: 'Server Error',
                    message: 'We\'re experiencing technical difficulties. Our team has been notified and is working to resolve the issue. Please try again later.'
                };
            case 503:
                return {
                    title: 'Service Unavailable',
                    message: 'The service is temporarily unavailable. Our team is aware and working to restore full feature. Please try again in a few moments.'
                };
            default:
                return {
                    title: 'Something Went Wrong',
                    message: `We encountered an unexpected error (${message.status}). Our system administrators have been automatically notified and are looking into it. Please try again later.`
                };
        }
    };

    const errorContent = getErrorMessage();

    return (
        <>{showErrorModal && 
            <>
                <div className="modal-backdrop fade show"></div>
                <div className="modal fade show" tabIndex="-1" aria-labelledby="saveSearchModalLabel" style={{ display: 'block' }} aria-hidden="false">
                    <div className={`modal-dialog ${modalSize} modal-dialog-centered`}>
                        <div className="modal-content position-relative">
                            <button type="button" className="position-absolute z-index-1 end-0 top-0 disc-4 bg-neutral-gray-900 disc-md-45 btn-close mt-n3 me-md-n35 me-n3" onClick={onClose} aria-label="Close"></button>
                            <div className="modal-body">
                                <div className="p-3">
                                    <h4 className="mb-3">{errorContent.title}</h4>
                                    <p className={message.status === 403 ? "mb-3" : "mb-0"}>{errorContent.message}</p>
                                    {message.status === 403 && (
                                        <Link
                                            to="/checkout/"
                                            className="btn btn-lg bg-crayola-red-500 w-100 text-decoration-none small fw-semi-bold rounded-2"
                                            onClick={onClose}
                                        >
                                            Add Subscription
                                        </Link>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>}
        </>
    );
}